import type { ReactNode } from 'react';
import type { BrandSourceTag } from '@mg/shared';
import { fieldPartIds } from './fieldParts';
import { SourceTags } from './SourceTags';

/**
 * One brand field's frame, in the wizard and on the Edit page (web.md §3.2):
 * 1. the label on the left and the source tags on the right (they wrap under it on a narrow column);
 * 2. the hint, UNDER THE LABEL (spec:209; the app's FormField puts it under the control, so it is not
 *    used here);
 * 3. the control, or `changes` in its place (Release 4's Show changes);
 * 4. the error, or else the counter;
 * 5. `footer` (Release 4's suggestion box).
 *
 * The parts carry the ids of fieldPartIds(id): give the control `aria-describedby` from
 * brandFieldDescribedBy (BrandTextField does it for itself). The label is a `<label for={id}>`; for a
 * control that is a group (Value philosophy's radio group) pass `group`, and the label becomes text
 * with the id `fieldPartIds(id).label` for the group's aria-labelledby.
 */
export function BrandFieldShell({
  id,
  label,
  group = false,
  hint,
  sources,
  aiSuggested = false,
  error,
  counter,
  readOnly = false,
  changes,
  footer,
  children,
}: {
  id: string;
  label: ReactNode;
  group?: boolean;
  hint?: string | null;
  sources: readonly BrandSourceTag[];
  aiSuggested?: boolean;
  error?: string | null;
  /** "1,650 of 2,000 characters"; not shown while there is an error, or while read-only. */
  counter?: string | null;
  readOnly?: boolean;
  /** Replaces the control (Release 4's Show changes). */
  changes?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const ids = fieldPartIds(id);
  const labelClass = 'text-sm font-medium text-ink-strong';
  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
        {group ? (
          <p id={ids.label} className={labelClass}>
            {label}
          </p>
        ) : (
          <label htmlFor={id} className={labelClass}>
            {label}
          </label>
        )}
        <SourceTags id={ids.sources} sources={sources} aiSuggested={aiSuggested} />
      </div>
      {hint && (
        <p id={ids.hint} className="mt-0.5 text-xs text-ink-faint">
          {hint}
        </p>
      )}
      <div className="mt-2">{changes ?? children}</div>
      {error ? (
        <p id={ids.error} className="mt-1 text-xs text-red-600 dark:text-error-dark">
          {error}
        </p>
      ) : counter && !readOnly ? (
        <p id={ids.counter} className="mt-1 text-xs text-ink-faint">
          {counter}
        </p>
      ) : null}
      {footer}
    </div>
  );
}
